import { Link } from "react-router-dom";
import { Building2, ShieldCheck, Target, Flag } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { COMPANY as C } from "../constants/company";
import { Eyebrow, Reveal, SectionTitle } from "../components/Primitives";

export default function About() {
  const { t } = useLang();
  const a = t.about;
  const rows = [
    [a.fields.brand, `${C.brand} · ${C.domain}`],
    [a.fields.legalName, C.legalName],
    [a.fields.nit, C.nit],
    [a.fields.registry, `${C.registry} — ${C.chamber}`],
    [a.fields.registeredAt, C.registeredAt],
    [a.fields.renewedAt, C.renewedAt],
    [a.fields.niif, C.niif],
    [a.fields.address, `${C.address}, ${C.city}, ${C.country}`],
    [a.fields.email, C.email],
    [a.fields.phones, C.phones.join(" · ")],
  ];

  return (
    <main data-testid="about-page" className="pt-28">
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal><Eyebrow>{a.eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.06}>
          <h1 data-testid="about-title" className="font-display mt-4 max-w-4xl text-4xl font-extrabold leading-[1.02] tracking-tight text-ink sm:text-5xl lg:text-6xl">{a.title}</h1>
        </Reveal>
        <Reveal delay={0.12}><p className="mt-6 max-w-3xl text-base leading-relaxed text-ink-2 sm:text-lg">{a.intro}</p></Reveal>

        <div className="mt-12 grid gap-4 lg:grid-cols-2">
          <Reveal>
            <div data-testid="about-status" className="card-soft h-full border-amber-200 bg-amber-50 p-7">
              <span className="eyebrow inline-flex items-center gap-2 text-amber-800"><Flag size={12} /> {a.statusTitle}</span>
              <p className="mt-4 text-base leading-relaxed text-amber-950">{a.status}</p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="card-soft h-full p-7">
              <span className="eyebrow inline-flex items-center gap-2 text-violet-700"><Target size={12} /> {a.missionTitle}</span>
              <p className="font-display mt-4 text-2xl font-bold leading-snug text-ink">{a.mission}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionTitle eyebrow="02" title={a.modelTitle} testId="about-model-title" />
          </div>
          <div className="grid gap-3 lg:col-span-7">
            {a.model.map((m, i) => (
              <Reveal key={m.t} delay={i * 0.06}>
                <div className="card-soft flex items-start gap-4 p-5">
                  <span className="font-display text-2xl font-extrabold text-violet-300">0{i + 1}</span>
                  <div>
                    <p className="font-display font-bold text-ink">{m.t}</p>
                    <p className="mt-1 text-sm text-ink-2">{m.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-night py-24 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal><Eyebrow className="text-pink-300">03 — {a.complianceTitle}</Eyebrow></Reveal>
          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {a.compliance.map((c, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div data-testid={`about-compliance-${i}`} className="flex h-full gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                  <ShieldCheck size={18} className="mt-0.5 shrink-0 text-pink-400" />
                  <p className="text-sm leading-relaxed text-violet-100/85">{c}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionTitle eyebrow="04" title={a.companyTitle} sub={a.contact} testId="about-company-title" />
          </div>
          <Reveal className="lg:col-span-7">
            <div data-testid="about-company-card" className="card-soft overflow-hidden">
              <div className="flex items-center gap-3 border-b border-line bg-lilac px-6 py-4">
                <Building2 size={18} className="text-violet-700" />
                <p className="font-display font-bold text-ink">{C.legalName}</p>
              </div>
              <dl className="divide-y divide-line">
                {rows.map(([k, v]) => (
                  <div key={k} className="grid gap-1 px-6 py-3.5 sm:grid-cols-3">
                    <dt className="text-xs font-semibold uppercase tracking-wider text-mist">{k}</dt>
                    <dd className="text-sm text-ink sm:col-span-2">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-28 sm:px-6 lg:px-8">
        <Reveal><Eyebrow>05 — {a.roadmapTitle}</Eyebrow></Reveal>
        <div className="mt-10 grid gap-px overflow-hidden rounded-3xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-5">
          {a.roadmap.map((r, i) => (
            <Reveal key={r.n} delay={i * 0.07} className="bg-white">
              <div data-testid={`about-roadmap-${i}`} className={`h-full p-6 ${r.now ? "bg-violet-50" : ""}`}>
                <div className="flex items-center justify-between">
                  <span className="eyebrow text-violet-600">{r.n}</span>
                  {r.now && <span className="eyebrow rounded-full bg-violet-600 px-2 py-0.5 text-[0.55rem] text-white">{a.current}</span>}
                </div>
                <p className="font-display mt-6 text-lg font-bold text-ink">{r.t}</p>
                <p className="mt-2 text-sm text-ink-2">{r.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <Link to="/legal/complaints" data-testid="about-contact-link" className="pill btn-primary mt-10 inline-flex px-6 py-3 text-sm font-semibold">{a.contactTitle} →</Link>
        </Reveal>
      </section>
    </main>
  );
}
